"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNovuBaseClient = exports.NovuRestResult = exports.NovuRedirectError = exports.NovuGatewayTimeoutError = exports.NovuServiceUnavailableError = exports.NovuBadGatewayError = exports.NovuNotImplementedError = exports.NovuInternalServerError = exports.NovuNotFoundError = exports.NovuForbiddenError = exports.NovuUnauthorizedError = exports.NovuBadRequestError = exports.HttpError = void 0;
// Base HttpError class with response field
class HttpError extends Error {
    responseText;
    status;
    response;
    constructor(responseText, status, response // Add response field
    ) {
        super(`${status}: ${responseText}`);
        this.responseText = responseText;
        this.status = status;
        this.response = response;
        this.name = this.constructor.name;
    }
    toString() {
        return `${this.name} (status: ${this.status}): ${this.responseText}`;
    }
}
exports.HttpError = HttpError;
// Specific error classes extending HttpError
class NovuBadRequestError extends HttpError {
}
exports.NovuBadRequestError = NovuBadRequestError;
class NovuUnauthorizedError extends HttpError {
}
exports.NovuUnauthorizedError = NovuUnauthorizedError;
class NovuForbiddenError extends HttpError {
}
exports.NovuForbiddenError = NovuForbiddenError;
class NovuNotFoundError extends HttpError {
}
exports.NovuNotFoundError = NovuNotFoundError;
class NovuInternalServerError extends HttpError {
}
exports.NovuInternalServerError = NovuInternalServerError;
class NovuNotImplementedError extends HttpError {
}
exports.NovuNotImplementedError = NovuNotImplementedError;
class NovuBadGatewayError extends HttpError {
}
exports.NovuBadGatewayError = NovuBadGatewayError;
class NovuServiceUnavailableError extends HttpError {
}
exports.NovuServiceUnavailableError = NovuServiceUnavailableError;
class NovuGatewayTimeoutError extends HttpError {
}
exports.NovuGatewayTimeoutError = NovuGatewayTimeoutError;
class NovuRedirectError extends HttpError {
    redirectUrl;
    constructor(responseText, status, redirectUrl, response) {
        super(responseText, status, response); // Pass response to the base class
        this.redirectUrl = redirectUrl;
    }
}
exports.NovuRedirectError = NovuRedirectError;
// Map of status codes to specific error classes
const errorMap = {
    400: NovuBadRequestError,
    401: NovuUnauthorizedError,
    403: NovuForbiddenError,
    404: NovuNotFoundError,
    500: NovuInternalServerError,
    501: NovuNotImplementedError,
    502: NovuBadGatewayError,
    503: NovuServiceUnavailableError,
    504: NovuGatewayTimeoutError,
};
// Result class for handling success and failure
class NovuRestResult {
    isSuccess;
    value;
    error;
    constructor(isSuccess, value, error) {
        this.isSuccess = isSuccess;
        this.value = value;
        this.error = error;
    }
    static ok(value) {
        return new NovuRestResult(true, value);
    }
    static fail(error) {
        return new NovuRestResult(false, undefined, error);
    }
    isSuccessResult() {
        return this.isSuccess;
    }
}
exports.NovuRestResult = NovuRestResult;
// Functional version of NovuBaseClient
const createNovuBaseClient = (baseUrl, headers = {}) => {
    const defaultHeaders = {
        'Content-Type': 'application/json',
        ...headers,
    };
    const buildUrl = (endpoint) => `${baseUrl}${endpoint}`;
    const safeFetch = async (url, fetchFunc) => {
        const response = await fetchFunc();
        if (response.ok) {
            const jsonData = await response.json();
            return NovuRestResult.ok(jsonData.data);
        }
        if (response.status >= 300 && response.status < 400) {
            const responseText = await getErrorResponse(response);
            const redirectError = new NovuRedirectError(responseText, response.status, response.headers.get('Location') || '', response // Pass the response object
            );
            return NovuRestResult.fail(redirectError);
        }
        const ErrorClass = errorMap[response.status] || HttpError;
        const responseText = await getErrorResponse(response);
        const errorResult = new ErrorClass(responseText, response.status, response); // Pass the response object
        return NovuRestResult.fail(errorResult);
    };
    async function getErrorResponse(response) {
        // Try to parse the response as JSON
        try {
            const json = await response.json();
            return JSON.stringify(json); // Return the JSON as a string
        }
        catch {
            // If JSON parsing fails, fallback to text response
            return await response.text();
        }
    }
    const safeGet = async (endpoint) => {
        return await safeFetch(endpoint, () => fetch(buildUrl(endpoint), {
            method: 'GET',
            headers: defaultHeaders,
        }));
    };
    const safePut = async (endpoint, data) => {
        return await safeFetch(endpoint, () => fetch(buildUrl(endpoint), {
            method: 'PUT',
            headers: defaultHeaders,
            body: JSON.stringify(data),
        }));
    };
    const safePost = async (endpoint, data) => {
        return await safeFetch(endpoint, () => fetch(buildUrl(endpoint), {
            method: 'POST',
            headers: defaultHeaders,
            body: JSON.stringify(data),
        }));
    };
    const safePatch = async (endpoint, data) => {
        return await safeFetch(endpoint, () => fetch(buildUrl(endpoint), {
            method: 'PATCH',
            headers: defaultHeaders,
            body: JSON.stringify(data),
        }));
    };
    const safeDelete = async (endpoint) => {
        return await safeFetch(endpoint, () => fetch(buildUrl(endpoint), {
            method: 'DELETE',
            headers: defaultHeaders,
        }));
    };
    return {
        safeGet,
        safePut,
        safePost,
        safeDelete,
        safePatch,
    };
};
exports.createNovuBaseClient = createNovuBaseClient;
//# sourceMappingURL=novu-base-client.js.map